import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { usePermissions } from '../../../contexts/PermissionContext';
import { useStateContext } from '../../../contexts/ContextProvider';
import { addDays } from '../../../domain/menage/joursPlanning';
import { useMesChambres, useReglagesAffichageMenage } from '../../api/useMesChambres';
import Button from '../../components/Button';
import EtatErreur from '../../components/EtatErreur';
import IndicateurActualisation from '../../components/IndicateurActualisation';
import Pills from '../../components/Pills';
import Screen from '../../components/Screen';
import Text from '../../components/Text';
import { echecSansDonnee } from '../../helpers/requete';
import { t } from '../../i18n/langue';
import { useTirerPourActualiser } from '../../hooks/useTirerPourActualiser';
import EnteteApp from '../../shell/EnteteApp';
import { useCoque } from '../../shell/CoqueContext';

import ListeDemain from './ListeDemain';
import SectionsDuJour from './SectionsDuJour';

/**
 * La liste de « Mes chambres » — lot 3 Odomo.
 *
 * Le parcours de l'agent du web (`MyRooms.tsx`), réécrit avec le socle : pas de
 * recherche ni de filtres (une journée tient sur un écran de téléphone), pas de
 * « Mes signalements », pas d'historique — arbitrages du plan.
 *
 * **Demain** suit la règle du web : le réglage `show_tomorrow_to_agents`, ou le
 * droit d'affecter les chambres. Il est en lecture seule.
 *
 * **Le retour d'un geste est dit en ligne** (`role=status`), pas par un toast : la
 * fiche revient ici avec un message dans l'état de navigation.
 */
type Jour = 'aujourdhui' | 'demain';

/** Libellés lus au rendu : l'app remonte au changement de langue. */
function jours(): { valeur: Jour; libelle: string }[] {
    return [
        { valeur: 'aujourdhui', libelle: t('menage.jours.aujourdhui') },
        { valeur: 'demain', libelle: t('menage.jours.demain') },
    ];
}

export default function ListeDuJour() {
    const { ouvrirPanneau } = useCoque();
    const { currentFacility } = useStateContext();
    const { hasPermission } = usePermissions();
    const message = (useLocation().state as { message?: string } | null)?.message;

    const facilityId = currentFacility?.id ?? null;
    const [jour, setJour] = useState<Jour>('aujourdhui');
    const { data, isLoading, isError, isFetching, refetch } = useMesChambres({ facilityId });
    const { data: reglages } = useReglagesAffichageMenage(facilityId);

    const demainVisible = !!reglages && (reglages.show_tomorrow_to_agents || hasPermission('housekeeping.assign'));
    const surDemain = demainVisible && jour === 'demain' && facilityId !== null;
    const echec = echecSansDonnee({ isError, data });
    const chambres = data ?? [];

    // Le geste de tirage n'est pas praticable avec VoiceOver : le lien « Actualiser »
    // reste le chemin accessible. L'indicateur tourne tant que la demande court.
    const refCorps = useRef<HTMLDivElement>(null);
    const [actualisationDemandee, setActualisationDemandee] = useState(false);
    const actualiser = () => {
        setActualisationDemandee(true);
        void refetch();
    };
    const { distance, pret } = useTirerPourActualiser(refCorps, actualiser, { actif: !surDemain });
    useEffect(() => {
        if (!isFetching) setActualisationDemandee(false);
    }, [isFetching]);

    return (
        <Screen
            refCorps={refCorps}
            sansRebond
            entete={<EnteteApp titre={t('menage.titre')} onOuvrirPanneau={ouvrirPanneau} />}
        >
            <IndicateurActualisation distance={distance} pret={pret} enCours={actualisationDemandee && isFetching} />

            <div className="flex flex-col gap-3 border-b border-border-color-light bg-white px-4 py-3">
                <Text variante="sousTitre" as="h2" className="truncate">
                    {currentFacility?.name ?? ''}
                </Text>
                {demainVisible && <Pills libelle={t('menage.choisirJour')} options={jours()} valeur={jour} onChange={setJour} />}
                {!surDemain && !isLoading && (
                    <Button libelle={t('menage.actualiser')} variante="lien" espacement="aucun" alignement="debut" onPress={actualiser} />
                )}
            </div>

            <div className="flex flex-col gap-3 px-4 py-4">
                {message && (
                    <div role="status" className="rounded-xl bg-main-color-light px-3 py-2">
                        <Text variante="petit" ton="marque">
                            {message}
                        </Text>
                    </div>
                )}

                {surDemain && reglages ? (
                    <ListeDemain facilityId={facilityId} date={addDays(reglages.today, 1)} />
                ) : (
                    <>
                        {isLoading && (
                            <Text variante="petit" ton="attenue">
                                {t('commun.chargement')}
                            </Text>
                        )}
                        {echec && <EtatErreur onReessayer={() => void refetch()} />}
                        {!isLoading && !echec && chambres.length === 0 && (
                            <Text variante="petit" ton="attenue">
                                {t('menage.aucuneChambreAujourdhui')}
                            </Text>
                        )}
                        {chambres.length > 0 && <SectionsDuJour chambres={chambres} />}
                    </>
                )}
            </div>
        </Screen>
    );
}
